import rentlioLocalStorage from 'Rentlio/helper/rentlioLocalStorage'
import { logErrorWithJsonData } from 'Rentlio/utils/logging'

const BILLING_DYNAMICS_KEY = 'billing-dynamics'

function roundTo2Decimals(value) {
  return Math.round(value * 100) / 100
}

// Maximum total mismatch (in cents) that is treated as a minor rounding difference
// rather than a critical price mismatch.
const MINOR_MISMATCH_THRESHOLD_CENTS = 3

function getTotalAmountCentsDifference(arr1, arr2) {
  const sumAmountCents = arr => arr.reduce((total, item) => total + (item?.amountCents || 0), 0)
  return Math.abs(Math.round(sumAmountCents(arr1)) - Math.round(sumAmountCents(arr2)))
}

function areArraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false

  for (let i = 0; i < arr1.length; i++) {
    const obj1 = arr1[i]
    const obj2 = arr2[i]

    for (const key in obj1) {
      const val1 = obj1[key]
      const val2 = obj2[key]

      if (typeof val1 === 'number' && typeof val2 === 'number') {
        if (roundTo2Decimals(val1) !== roundTo2Decimals(val2)) {
          return false
        }
      } else {
        if (val1 !== val2) {
          return false
        }
      }
    }
  }
  return true
}

export const saveBillingDynamics = billingDynamics => {
  rentlioLocalStorage.remove(BILLING_DYNAMICS_KEY)
  rentlioLocalStorage.add(BILLING_DYNAMICS_KEY, billingDynamics)
}

export const hasBillingDynamicsSaved = () => {
  return !!rentlioLocalStorage.get(BILLING_DYNAMICS_KEY)
}

export const validateBillingDynamics = (billingDynamics, receipt, bookingEngine) => {
  const storedBillingDynamics = rentlioLocalStorage.get(BILLING_DYNAMICS_KEY)
  const hasStoredBillingDynamics = Array.isArray(storedBillingDynamics) && storedBillingDynamics.length > 0
  const hasBillingDynamics = Array.isArray(billingDynamics) && billingDynamics.length > 0

  if (hasStoredBillingDynamics && hasBillingDynamics) {
    const areEqual = areArraysEqual(storedBillingDynamics, billingDynamics)

    // Known issue: pricing dynamics on the reservation confirmation screen do not include extras pricing.
    // Sentry logging is suppressed for this case until the root cause is resolved. Tracked in:
    // https://www.notion.so/rentlio/BUG-on-RBE-Pricing-dynamics-on-reservation-confirmation-screen-does-not-contain-service-prices-321f7425b15480d783abf58b0b3ef2f5?v=1569156847c844a0869b6bba0927061b&source=copy_link
    const receiptHasExtras = receipt?.extras?.items?.length > 0

    if (!areEqual && !receiptHasExtras) {
      const amountCentsDifference = getTotalAmountCentsDifference(storedBillingDynamics, billingDynamics)
      const isMinorMismatch = amountCentsDifference < MINOR_MISMATCH_THRESHOLD_CENTS

      const jsonData = {
        amountCentsDifference,
        paymentPageBillingDynamics: storedBillingDynamics,
        confirmationPageBillingDynamics: billingDynamics,
        receipt,
        bookingEngine: {
          id: bookingEngine.id,
          slug: bookingEngine.slug
        }
      }

      if (isMinorMismatch) {
        logErrorWithJsonData(`minor F-B price mismatch < ${MINOR_MISMATCH_THRESHOLD_CENTS} cents`, jsonData, {
          critical: 'false',
          description: `The price on the confirmation page differs from the payment page by less than ${MINOR_MISMATCH_THRESHOLD_CENTS} cents. This is most likely a rounding difference between frontend and backend price calculation. The reservation was successfully created.`
        })
      } else {
        logErrorWithJsonData(`F-B price mismatch > ${MINOR_MISMATCH_THRESHOLD_CENTS}`, jsonData, {
          critical: 'true',
          description:
            'The price displayed on the confirmation page does not match the price shown on the payment page. The reservation was successfully created, and the receipt has been logged along with this exception.'
        })
      }
    }
    rentlioLocalStorage.remove(BILLING_DYNAMICS_KEY)
  }
}
